import { Accordion, AccordionDetails, AccordionSummary, Typography, createStyles, makeStyles } from '@material-ui/core';
import cn from 'classnames';
import React, { ReactElement } from 'react';
import { TranquiTheme } from '../containers/App/theme';
import { FunderAgreement } from './getAgreementFromFunder';
import { getJcapAvalPayCenterPaymentSteps, getJcapDaviplataPaymentSteps } from './getJcapGuideSteps';
import { getImageUrl } from './imagesRoot';
import MarkdownRender from '../dictionaries/MarkdownRender';

const useStyles = makeStyles((theme: TranquiTheme) => createStyles({
  root: {
    padding: theme.spacing(3, 0),
  },
  accordion: {
    boxShadow: theme.shadows[5],
    borderBottom: `solid 1px ${theme.palette.indigo.main}40`,
    borderRadius: theme.spacing(0.5),
    overflow: 'hidden',
  },
  accordionSelected: {
    border: `solid 1px ${theme.palette.indigo.main}40`,
    '& $accordionTitleCard': {
      background: theme.palette.indigo.main,
      '& $accordionTitle': {
        color: theme.palette.pearl.main,
      },
    },
  },
  accordionTitleCard: {
    transition: 'all .4s',
  },
  accordionTitle: {
    ...theme.typography.body1,
    fontWeight: 600,
    color: theme.palette.indigo.main,
    transition: 'all .4s',
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    backgroundColor: theme.palette.marble.main,
    padding: theme.spacing(2),
    '& typography': {
      margin: 0,
    },
  },
  ol: {
    margin: 0,
    padding: 0,
    overflowWrap: 'anywhere',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    listStylePosition: 'inside',
    '& li::marker': {
      fontWeight: 'bold',
    },
  },
  generalTextTitle: {
    ...theme.typography.body1,
    lineHeight: 'inherit',
  },
  dividerTitle: {
    marginTop: theme.spacing(2),
  },
  generalText: {
    ...theme.typography.subtitle1,
    lineHeight: 'inherit',
  },
  littleText: {
    ...theme.typography.subtitle2,
    lineHeight: 'inherit',
  },
  tinyText: {
    ...theme.typography.subtitle2,
    color: theme.palette.grey[500],
    '& a': {
      color: theme.palette.lavender.main,
      fontWeight: 600,
    },
  },
}));

type Agreement = FunderAgreement['agreements'][number];
type Guide = {
  [key in Agreement['type']]: ReactElement | null;
};
interface JcapPaymentGuidesProps { agreement: Agreement }

export const JcapPaymentGuides: React.FC<JcapPaymentGuidesProps> = (props) => {
  const { agreement } = props;
  const classes = useStyles();
  const [ expanded, setExpanded ] = React.useState<string | false>(false);

  const handleChangeAccordion = (panel: string) => (_event: any, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const { type, value, link } = agreement;

  const getSteps = (() => {
    switch (type) {
      case 'AvalPayCenter': return getJcapAvalPayCenterPaymentSteps;
      case 'Daviplata': return getJcapDaviplataPaymentSteps;
      default: return undefined;
    }
  })();

  const steps = getSteps?.(value, link);

  if (!steps?.length) return null;

  const daviPlataPaymentGuide = (
    <Accordion
      className={cn(classes.accordion, expanded === `${type}` && classes.accordionSelected)}
      onChange={handleChangeAccordion(`${type}`)}
      expanded={expanded === `${type}`}
    >
      <AccordionSummary className={classes.accordionTitleCard}>
        <Typography className={classes.accordionTitle}>
          Cómo pagar con {type}
        </Typography>
      </AccordionSummary>
      <AccordionDetails className={classes.details}>
        <ol className={classes.ol}>
          {steps.map(({ text, image }) => {
            const img = !!image && getImageUrl(image);
            return (
              <li>
                <MarkdownRender text={text} />
                {!!img && (<img src={img} alt={text} width="100%" />)}
              </li>
            );
          })}
        </ol>
      </AccordionDetails>
    </Accordion>
  );

  const guides = {
    Daviplata: daviPlataPaymentGuide,
    AvalPayCenter: daviPlataPaymentGuide,
  } as Guide;

  return guides[agreement.type];
};
